'use client';

import { FC, useEffect, useMemo, useState } from 'react';

import { AddressLocation } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { List } from '@ravago/shared/radiance/components/list/components/List/List';
import { ListItem } from '@ravago/shared/radiance/components/list/components/ListItem/ListItem';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { APIProvider } from '@vis.gl/react-google-maps';

import { AppUtils } from '../../../../utils/app/app.utils';
import { convertStringToLatLng } from '../../../../utils/location/location.utils';

import { AddressMapGoogle } from './address-map-google';
import { AddressMarker } from './address-marker';

interface AddressMapProps {
   locations: AddressLocation[];
   googleMapsApiKey?: string | null;
   defaultCenter?: string;
   defaultZoom?: number;
   showSearch?: boolean;
   rounded?: boolean;
   height?: number;
}

export const AddressMap: FC<AddressMapProps> = ({ googleMapsApiKey, locations = [], defaultCenter, defaultZoom, showSearch, rounded, height }) => {
   const translate = useTranslations();

   const [searchActive, setSearchActive] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');
   const [filteredLocations, setFilteredLocations] = useState<AddressLocation[]>(locations);
   const [activeMarker, setActiveMarker] = useState<string>();
   const [activeMarkerLocation, setActiveMarkerLocation] = useState<AddressLocation>();

   const latLng = useMemo(() => convertStringToLatLng(defaultCenter), [defaultCenter]);

   const { phone, tabPort } = useBreakpoint();

   const searchEnabled = useMemo(() => !phone && !tabPort && showSearch, [phone, tabPort, showSearch]);

   const markerId = (index: number) => `marker-${index}`;

   const handleSearchItemClick = (location: AddressLocation) => {
      const locationIndex = locations.findIndex((loc) => loc.title === location.title && loc.formattedAddress === location.formattedAddress);
      if (locationIndex !== -1) {
         setActiveMarker(markerId(locationIndex));
         setActiveMarkerLocation(locations[locationIndex]);
      }
   };

   useEffect(() => {
      if (!searchTerm?.length) {
         setFilteredLocations(locations);
         return;
      }

      setFilteredLocations(
         locations.filter(
            ({ title, formattedAddress }) =>
               title.toLowerCase().includes(searchTerm.toLowerCase()) || formattedAddress?.toLowerCase().includes(searchTerm.toLowerCase())
         )
      );
   }, [searchTerm]);

   // Do not render the map if no API key is provided.
   if (!googleMapsApiKey) return null;

   return (
      <APIProvider apiKey={googleMapsApiKey}>
         <Box borderRadius={rounded ? 'sm' : undefined} overflowX="hidden">
            <AddressMapGoogle
               latLng={latLng}
               locations={locations}
               defaultZoom={defaultZoom}
               height={height}
               activeMarkerLocation={activeMarkerLocation}
            >
               {searchEnabled && (
                  <Box position="absolute" left={16} top={16} width="calc(70% - 48px)">
                     <InputField
                        label={translate('addresses.find-location')}
                        trailingIcon="search"
                        onFocus={() => setSearchActive(true)}
                        onBlur={() => setSearchActive(false)}
                        onChange={setSearchTerm}
                     />
                     {searchActive && (
                        <Box
                           position="absolute"
                           top={55}
                           direction="column"
                           gap="sm"
                           borderRadius="sm"
                           maxHeight={200}
                           overflowX="auto"
                           background="white"
                           width="100%"
                        >
                           <List>
                              {filteredLocations.map((location, index) => (
                                 <ListItem
                                    key={AppUtils.getKey('location', index)}
                                    supportingText={location.formattedAddress}
                                    onClick={() => handleSearchItemClick(location)}
                                 >
                                    <Typography variant="small">{location.title}</Typography>
                                 </ListItem>
                              ))}
                           </List>
                        </Box>
                     )}
                  </Box>
               )}

               {locations.map((location, index) => (
                  <AddressMarker
                     key={AppUtils.getKey('location-marker', index)}
                     {...location}
                     active={activeMarker === markerId(index)}
                     onClick={() => {
                        setActiveMarker(markerId(index));
                        setActiveMarkerLocation(undefined);
                     }}
                     onClose={() => {
                        setActiveMarker(undefined);
                        setActiveMarkerLocation(undefined);
                     }}
                  />
               ))}
            </AddressMapGoogle>
         </Box>
      </APIProvider>
   );
};
